import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'systemapp-detail',
  templateUrl: './systemapp-detail.component.html',
  styleUrls: ['./systemapp-detail.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzFormModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzDividerModule,
    NzGridModule,
    NzSelectModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class SystemappDetailComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  submitting = false;
  isAdd = true;
  systemAppId: string | null = null;
  secretKey: string | null = null;
  showId = false;
  showSecret = false;

  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  constructor(
    private readonly fb: FormBuilder,
    private readonly httpService: HttpService,
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      name: [null, [Validators.required]],
      systemCode: [null, [Validators.required]],
      description: [null, [Validators.required]]
    });
  }

  private readonly modalRef = inject(NzModalRef, { optional: true });
  private readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  ngOnInit(): void {
    if (this.nzModalData) {
      this.systemAppId = this.nzModalData.systemAppId || null;
      this.isAdd = this.nzModalData.isAdd !== undefined ? this.nzModalData.isAdd : true;
    }

    if (!this.isAdd && this.systemAppId) {
      this.loadItem(this.systemAppId);
    }
  }

  loadItem(id: string): void {
    this.loading = true;
    this.httpService
      .post(`${ConstantUtil.JOB_SYSTEMAPP_V1_ITEM}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.form.patchValue(res.data || {});
          this.secretKey = res.data?.secretKey ?? null;
          this.systemAppId = res.data?.id ?? this.systemAppId;
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('systemapp.detail.load.failed'));
        }
      });
  }

  save(): void {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    this.submitting = true;
    const requestParams: any = {
      ...this.form.value
    };
    if (!this.isAdd && this.systemAppId) {
      requestParams.id = this.systemAppId;
    }

    const apiUrl = this.isAdd ? ConstantUtil.JOB_SYSTEMAPP_V1_CREATE : ConstantUtil.JOB_SYSTEMAPP_V1_UPDATE;
    this.httpService
      .post(apiUrl, requestParams)
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.form.patchValue(res.data || {});
          this.secretKey = res.data?.secretKey ?? this.secretKey;
          this.message.success(this.isAdd ? this.i18n.fanyi('systemapp.create.success') : this.i18n.fanyi('systemapp.update.success'));
          if (this.isAdd && res.data?.id) {
            this.systemAppId = res.data.id;
            this.isAdd = false;
          }
          // Ensure ID stays updated after edit
          if (res.data?.id) {
            this.systemAppId = res.data.id;
          }
          this.cdr.detectChanges();
        } else {
          this.message.error(
            res.msg || (this.isAdd ? this.i18n.fanyi('systemapp.create.failed') : this.i18n.fanyi('systemapp.update.failed'))
          );
        }
      });
  }

  cancel(): void {
    if (this.modalRef) {
      this.modalRef.close(true);
    }
  }

  toggleId(): void {
    if (!this.systemAppId) return;
    this.showId = !this.showId;
  }

  toggleSecret(): void {
    if (!this.secretKey) return;
    this.showSecret = !this.showSecret;
  }

  copyText(text?: string | null): void {
    const value = text ?? '';
    if (!value) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => this.message.success(this.i18n.fanyi('common.copy-success')))
        .catch(() => this.fallbackCopy(value));
    } else {
      this.fallbackCopy(value);
    }
  }

  private fallbackCopy(value: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        this.message.success(this.i18n.fanyi('common.copy-success'));
      } else {
        this.message.error(this.i18n.fanyi('common.copy-failed'));
      }
    } catch (e) {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
    }
  }
}
